import { useEffect, useState } from 'react'
import { Alert, Box, Card, CardContent, FormControlLabel, Switch, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useStudentProfile } from '../../hooks/useStudentProfile'

/** Wires the Settings visibility toggle to the real `student_profiles.is_public` flag. */
export const ProfileVisibilityCard = () => {
  const navigate = useNavigate()
  const { profile, updateProfile } = useStudentProfile()
  const [pending, setPending] = useState<boolean | null>(null)
  const [justSaved, setJustSaved] = useState(false)

  const checked = pending ?? profile.data?.isPublic ?? false

  useEffect(() => {
    if (!justSaved) return
    const timeout = setTimeout(() => setJustSaved(false), 3000)
    return () => clearTimeout(timeout)
  }, [justSaved])

  const handleChange = (next: boolean) => {
    setPending(next)
    setJustSaved(false)
    updateProfile.mutate(
      { isPublic: next },
      {
        onSuccess: () => setJustSaved(true),
        onSettled: () => setPending(null),
      }
    )
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Profile visibility
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Public profiles appear in recruiter searches. While your profile is public, recruiters can
          view and export your contact information and your parent or guardian's. Turning this off
          hides you from all recruiters immediately — your data is kept and nothing else changes.
        </Typography>

        {updateProfile.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not update visibility. Please try again.
          </Alert>
        )}
        {justSaved && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Visibility updated.
          </Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={checked}
              disabled={profile.isLoading || updateProfile.isPending}
              onChange={e => handleChange(e.target.checked)}
            />
          }
          label={checked ? 'Visible to recruiters' : 'Hidden from recruiters'}
        />

        <Box sx={{ mt: 1 }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={() => navigate('/profile')}
          >
            See how your profile looks →
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}
